import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NbDialogRef } from '@nebular/theme';
import { Subscription, interval } from 'rxjs';
import { exhaustMap, finalize, first, switchMap, takeWhile, timeout } from 'rxjs/operators';
import { GeneratorService } from 'src/app/deebr/service/generator.service';

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const MAX_PRODUCT_IMAGES = 5;

@Component({
  selector: 'app-image-ambiance-dialog',
  templateUrl: './image-ambiance-dialog.component.html',
  styleUrls: ['./image-ambiance-dialog.component.scss'],
  standalone: false
})
export class ImageAmbianceDialogComponent implements OnInit, OnDestroy {
  @Input() imageUrl: string = '';
  @Input() productUrl: string = '';
  // Nebular affiche la carte en haut à gauche avant de la centrer : masquée jusqu'au 1er rendu (comme Animation IA)
  @HostBinding('class.loaded') isLoaded = false;
  readonly maxImages = MAX_PRODUCT_IMAGES;
  readonly skeletons = [1, 2, 3, 4]; // le scraper renvoie en général 4 à 5 vues
  productImages: string[] = [];
  mainImage: string = '';        // vue dont on garde l'angle (switch désactivé) ou vue principale (switch activé)
  extraImages: string[] = [];    // vues supplémentaires, envoyées seulement avec le switch activé
  loadedImages = new Set<string>();
  previewUrl?: string;
  viewUrl: string = '';
  isDraggingView: boolean = false;
  uploadingViews: number = 0;
  private addedViews = new Set<string>(); // vues ajoutées à la main : une erreur de chargement doit être signalée
  private emailRatio?: number;   // le résultat est recadré à ce ratio pour garder la grille de l'email
  scraping: boolean = false;
  useReferenceAngle: boolean = false;
  ambianceFile?: File;
  ambianceUrl: string = '';
  ambiancePreview: string = '';
  isDragging: boolean = false;
  progressStarted: boolean = false;
  resultUrl?: string;
  errorMessage?: string;
  progress: number = 0;
  private scrap?: Subscription;
  private polling?: Subscription;
  private ticker?: Subscription;

  constructor(
    private generatorService: GeneratorService,
    protected dialogRef: NbDialogRef<ImageAmbianceDialogComponent>,
  ) {}

  ngOnInit(): void {
    setTimeout(() => this.isLoaded = true);
    this.mainImage = this.imageUrl;
    this.scrapProductImages();
  }

  private scrapProductImages(): void {
    if (!/^https?:\/\//.test(this.productUrl)) return;
    this.scraping = true;
    this.scrap = this.generatorService.scrapImageProductPage(this.imageUrl, this.productUrl).pipe(
      switchMap(({ task_id }) => interval(2000).pipe(
        exhaustMap(() => this.generatorService.getTaskProgress(task_id)),
        first(task => task.status === 'SUCCESS' || task.status === 'FAILED')
      )),
      timeout(90000)
    ).subscribe({
      next: task => {
        const urls: string[] = Array.isArray(task.images?.urls) ? task.images.urls : [];
        const base = this.imageUrl.split('?')[0];
        // le scraper renvoie aussi des placeholders ("no image found") et souvent l'image de l'email elle-même
        const scraped = urls.filter(url => /^https?:\/\//.test(url) && url.split('?')[0] !== base);
        // on garde les vues déjà ajoutées à la main pendant le scraping
        this.productImages = [...new Set([...this.productImages, ...scraped])];
        this.scraping = false;
      },
      error: () => this.scraping = false,
    });
  }

  get views(): string[] {
    return [this.imageUrl, ...this.productImages];
  }

  // testé : avec plusieurs vues le modèle ne garde plus l'angle de la 1re, donc une seule vue quand le switch est désactivé
  get selectedImages(): string[] {
    return this.useReferenceAngle ? [this.mainImage, ...this.extraImages] : [this.mainImage];
  }

  onViewClick(url: string): void {
    if (!this.useReferenceAngle) {
      this.mainImage = url;
      this.extraImages = this.extraImages.filter(u => u !== url);
      return;
    }
    if (url === this.mainImage) return;
    const index = this.extraImages.indexOf(url);
    if (index >= 0) {
      this.extraImages.splice(index, 1);
    } else if (this.selectedImages.length < MAX_PRODUCT_IMAGES) {
      this.extraImages.push(url);
    }
  }

  onViewLoad(url: string, event: Event): void {
    this.loadedImages.add(url);
    if (url === this.imageUrl) {
      const img = event.target as HTMLImageElement;
      this.emailRatio = img.naturalWidth / img.naturalHeight;
    }
  }

  onViewError(url: string): void {
    if (this.addedViews.has(url)) this.errorMessage = "Impossible de charger cette image : vérifiez l'URL.";
    this.productImages = this.productImages.filter(u => u !== url);
    this.extraImages = this.extraImages.filter(u => u !== url);
    if (this.mainImage === url) this.mainImage = this.imageUrl;
  }

  onViewFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    Array.from(input.files ?? []).forEach(file => this.uploadView(file));
    input.value = ''; // permet de re-sélectionner le même fichier
  }

  onViewDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingView = false;
    Array.from(event.dataTransfer?.files ?? []).forEach(file => this.uploadView(file));
  }

  onViewUrlChange(): void {
    const url = this.viewUrl.trim();
    this.viewUrl = '';
    if (!url) return;
    if (!/^https?:\/\//.test(url)) {
      this.errorMessage = "L'URL de la vue doit commencer par http:// ou https://.";
      return;
    }
    this.errorMessage = undefined;
    this.addView(url);
  }

  // le back ne reçoit que des URLs de vues : un fichier local passe par l'upload temporaire (comme le GIF produit)
  private uploadView(file: File): void {
    const error = this.fileError(file);
    if (error) {
      this.errorMessage = error;
      return;
    }
    this.errorMessage = undefined;
    this.uploadingViews++;
    this.generatorService.uploadFile(file).pipe(
      finalize(() => this.uploadingViews--)
    ).subscribe({
      next: url => this.addView(url),
      error: () => this.errorMessage = "L'image n'a pas pu être importée.",
    });
  }

  // une vue ajoutée à la main est sélectionnée d'office : c'est celle que l'utilisateur veut utiliser
  private addView(url: string): void {
    if (!this.views.includes(url)) {
      this.productImages.push(url);
      this.addedViews.add(url);
    }
    if (!this.selectedImages.includes(url)) this.onViewClick(url);
  }

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.setFile(input.files?.[0]);
    input.value = ''; // permet de re-sélectionner le même fichier
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    this.setFile(event.dataTransfer?.files?.[0]);
  }

  onUrlChange(): void {
    this.ambianceFile = undefined;
    this.setPreview(this.ambianceUrl.trim());
  }

  private fileError(file: File): string | undefined {
    if (!file.type.startsWith('image/')) return 'Le fichier doit être une image (jpeg, png ou webp).';
    if (file.size > MAX_FILE_SIZE) return 'Image trop lourde (15 Mo max).';
    return undefined;
  }

  private setFile(file?: File): void {
    if (!file) return;
    const error = this.fileError(file);
    if (error) {
      this.errorMessage = error;
      return;
    }
    this.errorMessage = undefined;
    this.ambianceFile = file;
    this.ambianceUrl = '';
    this.setPreview(URL.createObjectURL(file));
  }

  private setPreview(src: string): void {
    if (this.ambiancePreview.startsWith('blob:')) URL.revokeObjectURL(this.ambiancePreview);
    this.ambiancePreview = src;
  }

  get canGenerate(): boolean {
    return !!this.ambianceFile || !!this.ambianceUrl.trim();
  }

  onGenerate(): void {
    const ambiance = this.ambianceFile ?? this.ambianceUrl.trim();
    if (!ambiance) return;
    this.progressStarted = true;
    this.errorMessage = undefined;
    this.resultUrl = undefined;
    this.startProgress();

    this.polling?.unsubscribe();
    this.polling = this.generatorService.ambianceImage(this.selectedImages, ambiance, !this.useReferenceAngle, this.emailRatio).pipe(
      switchMap(({ task_id }) => interval(3000).pipe(
        exhaustMap(() => this.generatorService.ambianceImageProgress(task_id)),
        takeWhile(status => status.status === 'RUNNING', true)
      ))
    ).subscribe({
      next: status => {
        if (status.status === 'SUCCEEDED') {
          this.ticker?.unsubscribe();
          this.resultUrl = status.image_url;
        } else if (status.status === 'FAILED') {
          this.fail("L'image n'a pas pu être générée. Elle ne respecte peut-être pas les guidelines de notre IA.");
        }
      },
      error: (err: HttpErrorResponse) => this.fail(err.error?.message || 'Impossible de lancer la mise en ambiance.'),
    });
  }

  // Vertex ne renvoie aucun avancement : barre estimée sur le temps écoulé (~25 s en moyenne), plafonnée à 95 %
  private startProgress(): void {
    const start = Date.now();
    this.progress = 0;
    this.ticker?.unsubscribe();
    this.ticker = interval(500).subscribe(() => {
      this.progress = 95 * (1 - Math.exp(-(Date.now() - start) / 20000));
    });
  }

  // retour au formulaire pour pouvoir relancer avec une autre image
  private fail(message: string): void {
    this.ticker?.unsubscribe();
    this.errorMessage = message;
    this.progressStarted = false;
  }

  onUse(): void {
    this.dialogRef.close(this.resultUrl);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.scrap?.unsubscribe();
    this.polling?.unsubscribe();
    this.ticker?.unsubscribe();
    this.setPreview('');
  }
}
